import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { fetchJson } from '../../api.ts';

import { useViewQueryState } from './useViews.ts';

/**
 * Polls for any in-flight onboarding run for the active org. Used by
 * the `OnboardingBanner` to surface a "step 2 of 4: defining
 * derivedContexts..." status while the cloud orchestrator works.
 *
 * Polling cadence: 5s while active (a typical onboarding takes 2–4
 * minutes), refetchInterval is unset when the latest fetched run is
 * terminal so we stop hammering the API once it's done. Stale time of
 * 0 forces the next refetch to actually request rather than serve from
 * cache.
 */

export interface OnboardingEvent {
  kind: string;
  at: string;
  // Other fields vary by kind; the banner treats them as optional and
  // surfaces only `kind`, `step`, counts, and the most recent ones.
  step?: string;
  count?: number;
  error?: string;
  [k: string]: unknown;
}

export interface OnboardingProgress {
  events?: OnboardingEvent[];
}

export interface OnboardingRun {
  id: string;
  repository_id: string | null;
  installation_id: number | null;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  current_step: 'signals' | 'bfs' | 'derivedContexts' | 'engineers' | 'observations' | null;
  step_progress: OnboardingProgress | null;
  started_at: string;
  completed_at: string | null;
  last_activity_at: string | null;
  error_kind: string | null;
  error_message: string | null;
}

export interface OnboardingListResponse {
  onboardings: Array<Pick<
    OnboardingRun,
    'id' | 'repository_id' | 'status' | 'current_step' | 'started_at' | 'completed_at' | 'error_kind' | 'error_message'
  >>;
}

export function useActiveOnboarding(): UseQueryResult<OnboardingRun | null, Error> {
  const { useMock, slug } = useViewQueryState();
  const qc = useQueryClient();
  const wasRunningRef = useRef(false);

  const query = useQuery<OnboardingRun | null, Error>({
    queryKey: ['onboarding', 'latest', slug],
    enabled: !useMock && slug !== null,
    queryFn: async () => {
      if (slug === null) return null;
      const list = await fetchJson<OnboardingListResponse>(
        `/v1/orgs/${slug}/onboardings?limit=1`,
      );
      const latest = list.onboardings[0];
      if (!latest) return null;
      // Fetch the full single-run detail (with step_progress events)
      // only when there's a recent run. The list endpoint omits the
      // event timeline for size.
      const detail = await fetchJson<OnboardingRun>(
        `/v1/orgs/${slug}/onboardings/${latest.id}`,
      );
      return detail;
    },
    // Poll every 5s when a run is in flight; stop once terminal.
    // refetchInterval is evaluated against the latest query data, so
    // a `succeeded`/`failed` return shuts off the polling on the next
    // tick rather than dragging on indefinitely.
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      if (status === 'running' || status === 'queued') return 5_000;
      return false;
    },
    staleTime: 0,
  });

  // When an onboarding finishes, the cloud orchestrator has just written
  // a fresh catalog (BFs / derivedContexts / skills / clusters) + re-derived
  // observations. Invalidate the matrix views on the running → terminal
  // edge so they re-read instead of showing the pre-onboarding catalog.
  useEffect(() => {
    const status = query.data?.status;
    const running = status === 'running' || status === 'queued';
    if (wasRunningRef.current && !running && status) {
      void qc.invalidateQueries({ queryKey: ['view'] });
    }
    wasRunningRef.current = running;
  }, [query.data?.status, qc]);

  return query;
}
